import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Button } from '../../components/ui/Button'
import { EmptyState } from '../../components/ui/EmptyState'
import { PermissionGate } from '../../components/PermissionGate'
import { documentationApi } from '../../api/documentation.api'
import { DocumentEditor } from './DocumentEditor'
import { NewDocumentModal } from './NewDocumentModal'
import styles from './Documentation.module.css'

export function DocumentationTab({ projectId }: { projectId: string }) {
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [showNew, setShowNew] = useState(false)

  const docsQuery = useQuery({ queryKey: ['documentation', 'list', projectId], queryFn: async () => (await documentationApi.list(projectId)).data })

  useEffect(() => {
    if (!selectedId && docsQuery.data && docsQuery.data.length > 0) {
      setSelectedId(docsQuery.data[0]!.id)
    }
  }, [docsQuery.data, selectedId])

  if (docsQuery.data && docsQuery.data.length === 0 && !showNew) {
    return (
      <EmptyState
        icon="📄"
        title="Noch keine Dokumentation"
        description="Lege das erste Dokument für dieses Projekt an — z.B. Architektur, API-Doku oder Installationsanleitung."
        action={
          <PermissionGate anyOf={['documentation.edit']}>
            <Button variant="primary" onClick={() => setShowNew(true)}>
              + Neues Dokument
            </Button>
          </PermissionGate>
        }
      />
    )
  }

  return (
    <div className={styles.layout}>
      <div>
        <PermissionGate anyOf={['documentation.edit']}>
          <Button variant="primary" size="sm" fullWidth onClick={() => setShowNew(true)} style={{ marginBottom: 'var(--space-3)' }}>
            + Neues Dokument
          </Button>
        </PermissionGate>
        <div className={styles.sidebar}>
          {docsQuery.data?.map((doc) => (
            <div
              key={doc.id}
              className={[styles.docItem, doc.id === selectedId ? styles.docItemActive : ''].join(' ')}
              onClick={() => setSelectedId(doc.id)}
            >
              {doc.title}
              {doc.category ? <span className={styles.docCategory}>{doc.category}</span> : null}
            </div>
          ))}
        </div>
      </div>

      <div>
        {selectedId ? (
          <DocumentEditor
            documentId={selectedId}
            projectId={projectId}
            onDeleted={() => setSelectedId(docsQuery.data?.find((d) => d.id !== selectedId)?.id ?? null)}
          />
        ) : (
          <EmptyState mascot title="Kein Dokument ausgewählt" description="Wähle links ein Dokument aus." />
        )}
      </div>

      {showNew ? <NewDocumentModal projectId={projectId} onClose={() => setShowNew(false)} onCreated={setSelectedId} /> : null}
    </div>
  )
}
